import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "../i18n";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { lang, l, data, t } = useLang();
  const { SITE, NAV_LINKS } = data;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const isOverHero = (location.pathname === "/" || location.pathname === "/en") && !scrolled;

  const itPath = location.pathname.replace(/^\/en(?=\/|$)/, "") || "/";
  const enPath = itPath === "/" ? "/en" : `/en${itPath}`;

  const langSwitch = (
    <span className="flex items-center gap-1.5 text-[11px] tracking-[0.14em] font-semibold" aria-label="Language selector">
      <Link
        to={itPath}
        data-testid="lang-switch-it"
        aria-current={lang === "it" ? "true" : undefined}
        className={`transition-colors duration-300 ${lang === "it" ? (isOverHero ? "text-cream border-b border-cream" : "text-sage border-b border-sage") : isOverHero ? "text-cream/60 hover:text-cream" : "text-body hover:text-sage"}`}
      >
        IT
      </Link>
      <span className={isOverHero ? "text-cream/40" : "text-sand"} aria-hidden="true">/</span>
      <Link
        to={enPath}
        data-testid="lang-switch-en"
        aria-current={lang === "en" ? "true" : undefined}
        className={`transition-colors duration-300 ${lang === "en" ? (isOverHero ? "text-cream border-b border-cream" : "text-sage border-b border-sage") : isOverHero ? "text-cream/60 hover:text-cream" : "text-body hover:text-sage"}`}
      >
        EN
      </Link>
    </span>
  );

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[80] focus:bg-sage focus:text-cream focus:px-4 focus:py-2 focus:rounded-full focus:text-sm"
      >
        {t.header.skipContent}
      </a>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
          scrolled
            ? "bg-cream/85 backdrop-blur-xl border-sand"
            : "bg-transparent border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between gap-4">
          <Link
            to={l("/")}
            data-testid="header-logo-link"
            className="flex items-center gap-3 shrink-0"
            aria-label={t.header.homeLabel}
          >
            <img src="/logo-chip.png" alt="" className="h-10 md:h-11 w-10 md:w-11 rounded-xl" />
            <span className={`font-serif text-lg md:text-2xl leading-none transition-colors duration-500 ${isOverHero ? "text-cream [text-shadow:0_1px_12px_rgba(0,0,0,0.45)]" : "text-ink"}`}>
              Le dolci <em className="italic">Dimore</em>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-7" aria-label={t.header.menuLabel}>
            {NAV_LINKS.map((item) => (
              <Link
                key={item.to}
                to={l(item.to)}
                data-testid={`nav-link-${item.to.slice(1)}`}
                className={`text-[11px] xl:text-xs uppercase tracking-[0.18em] whitespace-nowrap transition-colors duration-300 hover:text-terra ${
                  itPath === item.to ? "text-terra" : isOverHero ? "text-cream/90 [text-shadow:0_1px_10px_rgba(0,0,0,0.4)]" : "text-ink/80"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-5">
            <span className="hidden md:inline-flex">{langSwitch}</span>
            <a
              href={SITE.phoneHref}
              data-testid="header-phone-link"
              className={`hidden xl:block text-xs tracking-[0.12em] hover:text-terra transition-colors duration-300 ${isOverHero ? "text-cream/85 [text-shadow:0_1px_10px_rgba(0,0,0,0.4)]" : "text-ink/70"}`}
            >
              {SITE.phone.replace("+39 ", "")}
            </a>
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-booking-button"
              className="hidden sm:inline-flex items-center rounded-full bg-terra px-5 md:px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-cream transition-[background-color,transform] duration-300 hover:bg-terra-dark hover:-translate-y-0.5"
            >
              {t.header.book}
            </a>
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open-button"
              className={`lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border transition-colors duration-500 ${isOverHero ? "border-cream/40 bg-ink/20 backdrop-blur-sm" : "border-sand bg-cream/80"}`}
              aria-label={t.header.openMenu}
              aria-expanded={open}
            >
              <Menu className={`w-5 h-5 transition-colors duration-500 ${isOverHero ? "text-cream" : "text-ink"}`} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[70] bg-cream flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-label={t.header.menuLabel}
          >
            <div className="h-16 px-5 flex items-center justify-between border-b border-sand">
              <span className="flex items-center gap-3">
                <img src="/logo-chip.png" alt="" className="h-8 w-8 rounded-lg" />
                <span className="font-serif text-lg">Le dolci Dimore</span>
              </span>
              <span className="flex items-center gap-4">
                {langSwitch}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-menu-close-button"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-sand"
                  aria-label={t.header.closeMenu}
                >
                  <X className="w-5 h-5" />
                </button>
              </span>
            </div>
            <nav className="flex-1 overflow-y-auto px-6 py-8 flex flex-col gap-1" aria-label="Menu">
              {[{ to: "/", label: "Home" }, ...NAV_LINKS].map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={l(item.to)}
                    data-testid={`mobile-nav-link-${item.to === "/" ? "home" : item.to.slice(1)}`}
                    className="font-serif text-3xl py-3 block text-ink hover:text-terra transition-colors duration-300"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="px-6 pb-8 flex flex-col gap-3 border-t border-sand pt-6">
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-menu-booking-button"
                className="w-full inline-flex items-center justify-center rounded-full bg-terra px-6 py-4 text-sm font-semibold text-cream"
              >
                {t.header.bookStay}
              </a>
              <a
                href={SITE.phoneHref}
                data-testid="mobile-menu-phone-link"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-sage px-6 py-4 text-sm font-medium text-sage"
              >
                <Phone className="w-4 h-4" /> {SITE.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
